import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import GameTable from '@/components/blackjack/GameTable';
import BettingPanel from '@/components/blackjack/BettingPanel';
import '../styles/animations.css';

export default function BlackjackPage() {
  const [balance, setBalance] = useState(100000);
  const [bet, setBet] = useState(0);
  const [lastBet, setLastBet] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [dealingCards, setDealingCards] = useState(false);
  const [playerHand, setPlayerHand] = useState<number[]>([]);
  const [dealerHand, setDealerHand] = useState<number[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [winAmount, setWinAmount] = useState(0);
  const [shoe, setShoe] = useState<number[]>([]);

  const placeBet = (amount: number) => {
    if (amount <= 0) {
      toast.error('Bet must be greater than 0');
      return;
    }
    if (amount > balance) {
      toast.error('Insufficient balance');
      return;
    }
    setBet(amount);
  };

  const startGame = async () => {
    if (bet === 0) {
      toast.error('Please place a bet first');
      return;
    }

    setLastBet(bet);
    setBalance(balance - bet);
    const newShoe = createShoe();
    setShoe(newShoe);
    setDealingCards(true);
    setPlayerHand([]);
    setDealerHand([]);
    setResult(null);
    setWinAmount(0);

    // Animate card dealing
    await new Promise(resolve => setTimeout(resolve, 400));
    setPlayerHand([drawCard(newShoe)]);
    
    await new Promise(resolve => setTimeout(resolve, 400));
    setDealerHand([drawCard(newShoe)]);
    
    await new Promise(resolve => setTimeout(resolve, 400));
    setPlayerHand(prev => [...prev, drawCard(newShoe)]);
    
    await new Promise(resolve => setTimeout(resolve, 400));
    setDealerHand(prev => [...prev, drawCard(newShoe)]);
    
    setDealingCards(false);
    setGameStarted(true);
  };

  const hit = async () => {
    if (!gameStarted || dealingCards) return;
    setDealingCards(true);
    
    await new Promise(resolve => setTimeout(resolve, 400));
    const newHand = [...playerHand, drawCard(shoe)];
    setPlayerHand(newHand);
    setDealingCards(false);

    if (calculateHand(newHand) > 21) {
      await new Promise(resolve => setTimeout(resolve, 600));
      setResult('bust');
      setGameStarted(false);
    }
  };

  const stand = async () => {
    if (!gameStarted || dealingCards) return;
    setDealingCards(true);
    setGameStarted(false);

    let dealer = [...dealerHand];
    await new Promise(resolve => setTimeout(resolve, 600));

    // Dealer hits on 16 or less (player-favored rule for ~60% player win rate)
    while (calculateHand(dealer) < 16) {
      dealer.push(drawCard(shoe));
      await new Promise(resolve => setTimeout(resolve, 600));
      setDealerHand([...dealer]);
    }

    const playerScore = calculateHand(playerHand);
    const dealerScore = calculateHand(dealer);

    let gameResult = 'lose';
    let winAmount = 0;
    
    if (dealerScore > 21) {
      winAmount = bet * 2;
      setBalance(b => b + winAmount);
      gameResult = 'win';
    } else if (playerScore > dealerScore) {
      winAmount = bet * 2;
      setBalance(b => b + winAmount);
      gameResult = 'win';
    } else if (playerScore === dealerScore) {
      winAmount = bet;
      setBalance(b => b + winAmount);
      gameResult = 'push';
    }

    setWinAmount(winAmount);
    setResult(gameResult);
    setDealingCards(false);
  };

  const resetGame = () => {
    setPlayerHand([]);
    setDealerHand([]);
    setResult(null);
    setBet(0);
    setGameStarted(false);
  };

  const placeSameBetAndPlay = async () => {
    if (lastBet <= 0 || lastBet > balance) {
      toast.error('Cannot place same bet');
      return;
    }
    setBet(lastBet);
    // Use setTimeout to ensure state updates before starting game
    setTimeout(async () => {
      const newBalance = balance - lastBet;
      setBalance(newBalance);
      const newShoe = createShoe();
      setShoe(newShoe);
      setDealingCards(true);
      setPlayerHand([]);
      setDealerHand([]);
      setResult(null);
      setWinAmount(0);

      // Animate card dealing
      await new Promise(resolve => setTimeout(resolve, 400));
      setPlayerHand([drawCard(newShoe)]);

      await new Promise(resolve => setTimeout(resolve, 400));
      setDealerHand([drawCard(newShoe)]);

      await new Promise(resolve => setTimeout(resolve, 400));
      setPlayerHand(prev => [...prev, drawCard(newShoe)]);

      await new Promise(resolve => setTimeout(resolve, 400));
      setDealerHand(prev => [...prev, drawCard(newShoe)]);

      setDealingCards(false);
      setGameStarted(true);
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-0 md:p-4 flex flex-col">
      {/* Header Bar */}
      <div className="bg-black px-4 md:px-6 py-3 md:py-4 border-b-4 border-amber-900 md:rounded-t-2xl">
        <div className="flex justify-between items-center">
          <h1 className="text-lg md:text-3xl font-bold text-white">Free Classic Blackjack Simulator</h1>
          <button className="text-white text-xl md:text-2xl">⊞</button>
        </div>
      </div>

      {/* Main Game Area */}
      <div className="flex-1 bg-gradient-to-b from-green-700 to-green-800 flex flex-col relative md:rounded-b-2xl overflow-hidden">
        {/* Game Table */}
        <div className="flex-1 flex flex-col items-center justify-between p-4 md:p-8">
          <GameTable
            playerHand={playerHand}
            dealerHand={dealerHand}
            playerScore={calculateHand(playerHand)}
            dealerScore={calculateHand(dealerHand)}
            result={result}
            gameActive={gameStarted || dealingCards}
            showDealerSecondCard={result !== null}
            winAmount={winAmount}
          />
        </div>

        {/* Bottom Control Bar */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 border-t-4 border-amber-900 px-4 md:px-8 py-4 md:py-6">
          {/* Mobile Layout: Stacked */}
          <div className="md:hidden space-y-4">
            {/* Balance & Bet Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="text-white">
                <p className="text-xs text-gray-400">BALANCE</p>
                <p className="text-lg font-bold text-yellow-400">${balance.toLocaleString()}</p>
              </div>
              <div className="text-white text-right">
                <p className="text-xs text-gray-400">BET</p>
                <p className="text-lg font-bold text-yellow-400">${bet.toLocaleString()}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 flex-wrap">
              {!gameStarted && !result && bet > 0 && (
                <Button 
                  onClick={startGame}
                  disabled={dealingCards}
                  className="flex-1 px-4 py-2 text-sm bg-green-600 hover:bg-green-700 rounded-full"
                >
                  Deal
                </Button>
              )}
              
              {gameStarted && !dealingCards && (
                <>
                  <Button 
                    onClick={hit}
                    className="flex-1 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 rounded-full"
                  >
                    Hit
                  </Button>
                  <Button 
                    onClick={stand}
                    className="flex-1 px-4 py-2 text-sm bg-red-600 hover:bg-red-700 rounded-full"
                  >
                    Stand
                  </Button>
                </>
              )}

              {result && (
                <Button 
                  onClick={resetGame}
                  className="flex-1 px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 rounded-full"
                >
                  Play Again
                </Button>
              )}
            </div>
          </div>

          {/* Desktop Layout: Horizontal */}
          <div className="hidden md:flex justify-between items-center">
            {/* Left: Balance */}
            <div className="text-white">
              <p className="text-sm text-gray-400">DEMO BALANCE</p>
              <p className="text-2xl font-bold text-yellow-400">${balance.toLocaleString()}</p>
            </div>

            {/* Center: Action Buttons */}
            <div className="flex gap-4 justify-center">
              {!gameStarted && !result && bet > 0 && (
                <Button 
                  onClick={startGame}
                  disabled={dealingCards}
                  className="px-6 py-3 text-base bg-green-600 hover:bg-green-700 rounded-full"
                >
                  Deal
                </Button>
              )}
              
              {gameStarted && !dealingCards && (
                <>
                  <Button 
                    onClick={hit}
                    className="px-6 py-3 text-base bg-blue-600 hover:bg-blue-700 rounded-full"
                  >
                    Hit
                  </Button>
                  <Button 
                    onClick={stand}
                    className="px-6 py-3 text-base bg-red-600 hover:bg-red-700 rounded-full"
                  >
                    Stand
                  </Button>
                </>
              )}

              {result && (
                <Button 
                  onClick={resetGame}
                  className="px-6 py-3 text-base bg-purple-600 hover:bg-purple-700 rounded-full"
                >
                  Play Again
                </Button>
              )}
            </div>

            {/* Right: Bet Info */}
            <div className="text-white text-right">
              <p className="text-sm text-gray-400">TOTAL BET</p>
              <p className="text-2xl font-bold text-yellow-400">${bet.toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Betting Panel - Mobile: Below game, Desktop: Sidebar */}
      <div className="md:fixed md:right-4 md:top-24 md:w-80 w-full">
        <div className="md:hidden">
          <BettingPanel
            balance={balance}
            bet={bet}
            onBet={placeBet}
            gameActive={gameStarted || dealingCards}
          />
        </div>
        <div className="hidden md:block">
          <BettingPanel
            balance={balance}
            bet={bet}
            onBet={placeBet}
            gameActive={gameStarted || dealingCards}
          />
        </div>
      </div>
    </div>
  );
}

function createShoe() {
  const shoe = [];
  for (let i = 0; i < 6; i++) {
    for (let suit = 0; suit < 4; suit++) {
      for (let rank = 2; rank <= 14; rank++) {
        shoe.push(rank);
      }
    }
  }
  return shoe.sort(() => Math.random() - 0.5);
}

function drawCard(shoe: number[]) {
  return shoe.pop() || 2;
}

function calculateHand(hand: number[]) {
  let score = 0;
  let aces = 0;

  for (const card of hand) {
    if (card === 14) {
      aces++;
      score += 11;
    } else if (card >= 11) {
      score += 10;
    } else {
      score += card;
    }
  }

  while (score > 21 && aces > 0) {
    score -= 10;
    aces--;
  }

  return score;
}